/**
 * TaskPeekDialog — Iter Feb 2026 · R66.
 *
 * Shared popup that shows the top open tasks for a single project. Reused
 * on the Projects list and the Executive Portfolio dashboard so reps can
 * peek at task health without navigating away from the review context.
 *
 * Props:
 *   open, onOpenChange
 *   wsId          — workspace id (uses tasks-search endpoint)
 *   projectId     — the project to peek into
 *   projectName   — for the header
 *   onAddTask     — optional (mail, project) => void; adds a footer button
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, ExternalLink, Plus, ListTodo } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";

const STATUS_CHIP = {
  open:         "border-neutral-400 text-neutral-700 bg-neutral-50",
  in_progress:  "border-sky-500 text-sky-800 bg-sky-50",
  blocked:      "border-rose-500 text-rose-800 bg-rose-50",
  done:         "border-emerald-500 text-emerald-800 bg-emerald-50",
};

function _isOverdue(t) {
  return t.due_date && t.status !== "done"
         && new Date(t.due_date) < new Date(new Date().toISOString().slice(0, 10));
}

export default function TaskPeekDialog({
  open, onOpenChange, wsId, projectId, projectName, onAddTask,
}) {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open+in_progress");

  useEffect(() => {
    if (!open || !wsId || !projectId) { setRows(null); return; }
    let cancel = false;
    (async () => {
      setRows(null);
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/tasks-search`,
          { params: { project_id: projectId } },
        );
        if (!cancel) setRows(data || []);
      } catch (e) {
        if (!cancel) {
          setRows([]);
          toast.error(apiErrorMessage(e, "Task peek failed"));
        }
      }
    })();
    return () => { cancel = true; };
  }, [open, wsId, projectId]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (statusFilter === "all") return rows;
    if (statusFilter === "open+in_progress")
      return rows.filter(t => t.status === "open" || t.status === "in_progress");
    return rows.filter(t => t.status === statusFilter);
  }, [rows, statusFilter]);

  const counts = useMemo(() => {
    if (!rows) return null;
    return {
      total:       rows.length,
      open:        rows.filter(t => t.status === "open").length,
      in_progress: rows.filter(t => t.status === "in_progress").length,
      blocked:     rows.filter(t => t.status === "blocked").length,
      done:        rows.filter(t => t.status === "done").length,
      overdue:     rows.filter(_isOverdue).length,
    };
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col rounded-none"
        data-testid="task-peek-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ListTodo className="w-4 h-4" />
            <span className="truncate">Tasks · {projectName || "project"}</span>
          </DialogTitle>
        </DialogHeader>

        {counts && (
          <div className="flex items-center gap-1.5 flex-wrap text-[10px] shrink-0" data-testid="task-peek-counts">
            {[
              { v: "all",              label: `All · ${counts.total}` },
              { v: "open+in_progress", label: `Open · ${counts.open + counts.in_progress}` },
              { v: "blocked",          label: `Blocked · ${counts.blocked}` },
              { v: "done",             label: `Done · ${counts.done}` },
            ].map(o => (
              <button
                key={o.v}
                type="button"
                onClick={() => setStatusFilter(o.v)}
                className={`uppercase tracking-widest font-semibold px-2 py-0.5 border ${statusFilter === o.v ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted/40"}`}
                data-testid={`task-peek-filter-${o.v}`}
              >
                {o.label}
              </button>
            ))}
            {counts.overdue > 0 && (
              <span className="inline-flex items-center gap-1 border border-rose-400 bg-rose-50 text-rose-800 uppercase tracking-widest font-semibold px-2 py-0.5">
                <AlertTriangle className="w-2.5 h-2.5" /> {counts.overdue} overdue
              </span>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto border">
          {rows === null && (
            <div className="p-8 text-center text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading tasks…
            </div>
          )}
          {rows && filtered && filtered.length === 0 && (
            <div className="p-8 text-center text-xs text-muted-foreground italic">
              No tasks match this filter.
            </div>
          )}
          {filtered && filtered.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Title</th>
                  <th className="p-2 text-left w-32">Assignee</th>
                  <th className="p-2 text-left w-24">Due</th>
                  <th className="p-2 text-left w-28">Status</th>
                  <th className="p-2 text-right w-16">Progress</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const overdue = _isOverdue(t);
                  return (
                    <tr key={t.id}
                        className="border-t border-border hover:bg-muted/20"
                        data-testid={`task-peek-row-${t.id}`}>
                      <td className="p-2">
                        <Link
                          to={`/app/tasks?taskId=${t.id}`}
                          className="hover:underline flex items-center gap-1.5"
                        >
                          {overdue && <AlertTriangle className="w-3 h-3 text-rose-600 shrink-0" />}
                          <span className="truncate">{t.title}</span>
                        </Link>
                        {(t.tags || []).length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {t.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="text-[9px] border border-border px-1 text-muted-foreground">{tag}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-muted-foreground truncate">
                        {t.assignee_name || "—"}
                      </td>
                      <td className={`p-2 tabular-nums ${overdue ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>
                        {t.due_date || "—"}
                      </td>
                      <td className="p-2">
                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border ${STATUS_CHIP[t.status] || "border-border"}`}>
                          {(t.status || "").replace("_", " ")}
                        </span>
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        {t.progress_percent != null ? `${t.progress_percent}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t pt-2">
          <Link
            to={`/app/projects/${projectId}`}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-auto"
            data-testid="task-peek-open-project"
          >
            Open project <ExternalLink className="w-3 h-3" />
          </Link>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => onOpenChange?.(false)}
            data-testid="task-peek-close"
          >
            Close
          </Button>
          {onAddTask && (
            <Button
              size="sm"
              className="rounded-none"
              onClick={() => { onOpenChange?.(false); onAddTask(); }}
              data-testid="task-peek-add-task"
            >
              <Plus className="w-3 h-3 mr-1" /> Add task
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
